import {
	DEFAULT_ORDER_FIELD_IDS,
	EXCLUDED_FIELD_IDS_SET,
	PARENT_FIELD_ID,
} from '../common/constants';
import { ISSUE_TYPE_FIELD } from '../controllers/use-ai-create-project-context/constants';
import type {
	FieldConfiguration,
	ProjectLike,
} from '../controllers/use-ai-create-project-context/types';

/**
 * Builds a unique, ordered configuration of fields for a given project and suggested field IDs.
 *
 * This function collects field configurations from all issue types in the provided project,
 * filters out excluded fields, and prioritizes required fields and those present in the suggested field IDs.
 * The resulting configuration preserves the order of default field IDs, followed by required non-default fields,
 * and then other allowed fields, all while ensuring uniqueness.
 *
 * @param project - The project object containing issue types and their field configurations.
 * @param suggestFieldIds - A set of field IDs that are suggested for inclusion.
 * @param hasExistingParentIssueKey - Whether any draft work item has an existing parent issue key.
 * @returns A record mapping field IDs to their corresponding field configuration objects, ordered by priority.
 */
export function buildUniqueFieldsConfiguration(
	project: ProjectLike | null | undefined,
	suggestIssueTypeIds: ReadonlySet<string>,
	suggestFieldIds: ReadonlySet<string> | undefined,
	draftItemHasExistingParent = false,
): Record<string, FieldConfiguration> {
	const issueTypeEdges = project?.issueTypes?.edges;
	if (!issueTypeEdges?.length) {
		return {};
	}

	// Map preserves insertion order for first appearance; Set for quick membership.
	const fieldsMap = new Map<string, FieldConfiguration>();
	const seen = new Set<string>();

	for (const issueTypeEdge of issueTypeEdges) {
		const fieldEdges = issueTypeEdge?.node?.fieldsConfiguration?.edges;
		if (fieldEdges?.length) {
			for (const fieldEdge of fieldEdges) {
				const node = fieldEdge?.node;
				if (node) {
					const fieldId = node.fieldId;
					const excluded = !fieldId || EXCLUDED_FIELD_IDS_SET.has(fieldId);
					// If the draft has an existing parent issue key, we want to include the parent field to be displayed in the table,
					if (!excluded || (draftItemHasExistingParent && fieldId === PARENT_FIELD_ID)) {
						const isExistOnIssueType = suggestIssueTypeIds.has(
							issueTypeEdge?.node?.issueTypeId ?? '',
						);
						const isRequired = (node.fieldConfig?.isRequired && isExistOnIssueType) ?? false;
						const allowed =
							(isRequired && isExistOnIssueType) ||
							(suggestFieldIds ? suggestFieldIds.has(fieldId) : false);
						if (allowed) {
							const existing = fieldsMap.get(fieldId);
							if (!existing || (isRequired && !existing.isRequired)) {
								fieldsMap.set(fieldId, {
									__typename: node.__typename,
									fieldId,
									name: node.name,
									isEditable: node.fieldConfig?.isEditable ?? undefined,
									isRequired: isRequired || undefined,
									type: node.type,
								});
							}
							if (!seen.has(fieldId)) {
								seen.add(fieldId);
							}
						}
					}
				}
			}
		}
	}

	if (fieldsMap.size === 0) {
		// Even if no GraphQL-provided fields, we still inject the artificial issuetype field.
		return {
			issuetype: ISSUE_TYPE_FIELD,
		};
	}

	const ordered: Record<string, FieldConfiguration> = {};

	// 1. Defaults in declared order if present.
	for (const id of DEFAULT_ORDER_FIELD_IDS) {
		const fieldConfiguration = fieldsMap.get(id);
		if (
			fieldConfiguration &&
			!EXCLUDED_FIELD_IDS_SET.has(id) &&
			(fieldConfiguration.isRequired || (suggestFieldIds ? suggestFieldIds.has(id) : false))
		) {
			ordered[id] = fieldConfiguration;
		}
	}

	// 2 & 3. Iterate collected in insertion order to bucket leftover required vs others.
	const requiredNonDefault: FieldConfiguration[] = [];
	const remaining: FieldConfiguration[] = [];

	for (const [id, fieldConfiguration] of fieldsMap.entries()) {
		if (!ordered[id]) {
			// TODO: Only show required fields that exist for the issue type
			if (fieldConfiguration.isRequired) {
				requiredNonDefault.push(fieldConfiguration);
			} else if (suggestFieldIds ? suggestFieldIds.has(id) : false) {
				remaining.push(fieldConfiguration);
			}
		}
	}

	for (const fieldConfiguration of requiredNonDefault) {
		ordered[fieldConfiguration.fieldId] = fieldConfiguration;
	}
	for (const fieldConfiguration of remaining) {
		ordered[fieldConfiguration.fieldId] = fieldConfiguration;
	}

	const { issuetype: existingIssueType, ...rest } = ordered;
	return { issuetype: existingIssueType ?? ISSUE_TYPE_FIELD, ...rest };
}
